import React, { useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye, faHeadphones, faSearch, faSpinner, faVideo } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../api/axios';
import api from '../../api';
import EmptyPage from '../../components/EmptyPage';
import TablePagination from '../../components/Pagination';
import tableStyles from '../../styles/tableStyles';
import { readPath } from '../../seo/config';

const excerpt = (text, max = 160) => {
    const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return '';
    return cleaned.length > max ? `${cleaned.slice(0, max)}…` : cleaned;
};

const SearchResults = () => {
    const { t } = useTranslation();
    const tr = (key) => t(`searchPage:${key}`);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const query = (searchParams.get('q') || '').trim();
    const page = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);
    const limit = Math.min(Math.max(parseInt(searchParams.get('limit') || '10', 10) || 10, 1), 50);

    const queryParams = useMemo(() => ({ q: query, page, limit }), [query, page, limit]);

    const { data: response, isLoading } = useQuery({
        queryKey: ['catalog-search', queryParams],
        queryFn: () => api.search.searchCatalog(queryParams),
        enabled: query.length > 0,
        select: (data) => data?.data
    });

    const items = response?.data || [];
    const totalCount = response?.totalCount || 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / limit));

    const updateParams = (next) => {
        const params = new URLSearchParams(searchParams);
        Object.entries(next).forEach(([key, value]) => {
            if (value == null || value === '') {
                params.delete(key);
            } else {
                params.set(key, String(value));
            }
        });
        const queryString = params.toString();
        navigate(queryString ? `/search?${queryString}` : '/search');
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        updateParams({ page: newPage });
    };

    const handlePageSizeChange = (newLimit) => {
        if (newLimit < 1) return;
        updateParams({ limit: newLimit, page: 1 });
    };

    const [downloading, setDownloading] = React.useState(new Set());

    const handleDownload = async (item) => {
        if (!item.manual?.uri) return;
        setDownloading((prev) => new Set(prev).add(`${item.source}-${item.id}`));
        try {
            const res = await axiosInstance.get(`/fsmanuals/${item.manual.uri}`, {
                responseType: 'blob'
            });
            const fileBlob = new Blob([res.data], { type: res.data.type });
            const url = URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = item.title || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setDownloading((prev) => {
                const next = new Set(prev);
                next.delete(`${item.source}-${item.id}`);
                return next;
            });
        }
    };

    const actionLabel = (item) => {
        const type = String(item.documentType || '').toLowerCase();
        if (type.includes('video')) return t('contentCard:watch');
        if (type.includes('audio')) return t('contentCard:listen');
        return t('contentCard:read');
    };

    const actionIcon = (item) => {
        const type = String(item.documentType || '').toLowerCase();
        if (type.includes('video')) return faVideo;
        if (type.includes('audio')) return faHeadphones;
        return faEye;
    };

    return (
        <div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 mb-4">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-1">{tr('pageTitle')}</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    {query ? t('searchPage:resultsFor', { query }) : tr('pageDescription')}
                </p>
            </div>

            {query ? (
                <div className="text-sm text-gray-500 ml-2 mb-3 dark:text-gray-400">
                    {tr('showing')} {items.length} {tr('of')} {totalCount}
                </div>
            ) : null}

            {!query ? (
                <EmptyPage
                    colSpan={12}
                    isLoading={false}
                    icon={faSearch}
                    description={tr('emptyPrompt')}
                    placeholder={tr('emptyPlaceholder')}
                    loadingMessage={tr('searchLoadingMessage')}
                />
            ) : isLoading ? (
                <EmptyPage
                    colSpan={12}
                    isLoading
                    icon={faSearch}
                    description={tr('searchDescription')}
                    placeholder={tr('emptyPlaceholder')}
                    loadingMessage={tr('searchLoadingMessage')}
                />
            ) : items.length === 0 ? (
                <EmptyPage
                    colSpan={12}
                    isLoading={false}
                    icon={faSearch}
                    description={tr('searchDescription')}
                    placeholder={tr('emptyPlaceholder')}
                    loadingMessage={tr('searchLoadingMessage')}
                />
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                            {items.map((item) => {
                                const key = `${item.source}-${item.id}`;
                                const year = item.date ? new Date(item.date).getFullYear() : '';
                                const categoryLabel = t(`searchPage:categories.${item.categoryKey}`, {
                                    defaultValue: item.category
                                });
                                const isDownloading = downloading.has(key);
                                return (
                                    <li key={key} className="px-4 py-4 sm:px-5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                    <span className="inline-flex items-center rounded-full bg-[#41A6D7]/10 text-[#256B8A] dark:bg-[#41A6D7]/20 dark:text-[#7ec8e8] px-2.5 py-0.5 text-xs font-semibold">
                                                        {categoryLabel}
                                                    </span>
                                                    {year ? (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">{year}</span>
                                                    ) : null}
                                                </div>
                                                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                                                    <Link
                                                        to={readPath(item.id, item.title, item.documentType || item.material_type?.name)}
                                                        state={{ id: item.id, documentType: item.documentType || item.material_type?.name, url: item.url || null }}
                                                        className="hover:text-[#41A6D7]"
                                                    >
                                                        {item.title}
                                                    </Link>
                                                </h2>
                                                {item.description ? (
                                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                                        {excerpt(item.description)}
                                                    </p>
                                                ) : null}
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <Link
                                                    to={readPath(item.id, item.title, item.documentType || item.material_type?.name)}
                                                    state={{ id: item.id, documentType: item.documentType || item.material_type?.name, url: item.url || null }}
                                                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3590BA] dark:text-[#41A6D7] hover:underline"
                                                >
                                                    <FontAwesomeIcon icon={actionIcon(item)} />
                                                    {actionLabel(item)}
                                                </Link>
                                                {item.manual?.uri ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownload(item)}
                                                        disabled={isDownloading}
                                                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:underline disabled:opacity-60"
                                                    >
                                                        <FontAwesomeIcon icon={isDownloading ? faSpinner : faDownload} spin={isDownloading} />
                                                        {t('contentCard:download')}
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <TablePagination
                        totalPages={totalPages}
                        page={page}
                        limit={limit}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        tableStyles={tableStyles}
                    />
                </>
            )}
        </div>
    );
};

export default SearchResults;
